import _ from "lodash";
import { apolloClient } from "@/plugins/apollo";
import ability from "@/services/ability";

import api from "@/api/cloud";

import { dataURLtoFile } from "@/utils/dataUrlToFile";

import gql from "graphql-tag";

import getUserInfo from "@/graphql/users/getUserInfo.gql";
import getSingleStaff from "@/graphql/users/getSingleStaff.gql";
import getSingleContact from "@/graphql/users/getSingleContact.gql";
import getSingleResident from "@/graphql/users/getSingleResident.gql";

import updateStaff from "@/graphql/users/updateStaff.gql";
import updateContact from "@/graphql/users/updateContact.gql";
import updateResident from "@/graphql/users/updateResident.gql";

const residentPersonalInfoFields = [
	"firstName",
	"lastName",
	"address",
	"building",
	"primaryPhone",
	"secondaryPhone",
	"email",
	"birthdate",
	"gender",
];
const staffPersonalInfoFields = [
	"firstName",
	"lastName",
	"email",
	"jobTitle",
	"primaryPhone",
	"secondaryPhone",
];
const contactPersonalInfoFields = [
	"firstName",
	"lastName",
	"relationship",
	"email",
	"primaryPhone",
];
const moreInfoFields = ["moveInDate", "residenceType", "careSetting"];
const preferencesFields = ["checkin"];

const setProfileImageMutation = (userType) => gql`
	mutation setProfileImage($id: UUID!, $image: Upload!) {
		community(id: "0") {
			${userType}(id: $id) {
				setProfileImage(file: $image) {
					_id
					profileImage
				}
			}
		}
	}
`;

const state = () => ({
	editable: false,
	loading: false,
	userType: "",
	profileImage: "",
	userCommunities: [],

	data: {},
	updated: {},

	updatedProperties: {},

	// Raw SSO Data
	ssoRoles: [],
	ssoProperties: [],
	ssoAssociations: [],
});

const getters = {
	// control data
	canEdit: state => state.editable,
	advancedEdit:
		(state, g, rs, rootGetters) => state.editable && ability.can("edit", "Users"),
	loading: state => state.loading,
	type: state => state.userType,
	community: state => state.userCommunities[0],
	data: state => state.data,

	profileImage: state => state.profileImage,

	// individual form data fields
	residentPersonalInfo: state => _.pick(state.data, residentPersonalInfoFields),
	staffPersonalInfo: state => _.pick(state.data, staffPersonalInfoFields),
	contactPersonalInfo: state => _.pick(state.data, contactPersonalInfoFields),
	moreInfo: state => _.pick(state.data, moreInfoFields),
	preferences: state => _.pick(state.data, preferencesFields),

	updated: state => state.updated,
	updatedProperties: state => state.updatedProperties,

	// SSO Data
	roles: state => state.ssoRoles,
	properties: state => state.ssoProperties,
	associations: state => state.ssoAssociations,
};

const mutations = {
	SET_EDITABLE(state, editable) {
		state.editable = editable;
	},
	SET_LOADING(state, isLoading) {
		state.loading = isLoading;
	},
	SET_USER_TYPE(state, type) {
		state.userType = type;
	},
	SET_USER_COMMUNITIES(state, communities) {
		state.userCommunities = communities;
	},

	SET_UPDATED_DATA(state, data = ["", ""]) {
		state.updated[data[0]] = data[1];
	},
	SET_DATA(state, data = {}) {
		state.data = data;
	},

	SET_PROFILE_IMAGE(state, image) {
		state.profileImage = image;
	},

	SET_SSO_ROLES(state, roles) {
		state.ssoRoles = roles;
	},
	SET_SSO_PROPERTIES(state, properties) {
		state.ssoProperties = properties;
	},
	SET_UPDATED_SSO_PROPERTY(state, [key, value] = ["", ""]) {
		state.updatedProperties[key] = value;
	},
	DEL_UPDATED_SSO_PROPERTY(state, key) {
		delete state.updatedProperties[key];
	},
	SET_SSO_ASSOCIATIONS(state, associations) {
		state.ssoAssociations = associations;
	},

	RESET_UPDATED(state) {
		state.updated = {};
		state.updatedProperties = {};
	},
	RESET(s) {
		Object.assign(s, state());
	},
};

const actions = {
	async getUser({ commit, dispatch, getters }, id) {
		const res = await apolloClient.query({
			query: getUserInfo,
			variables: { id },
		});

		commit("SET_USER_TYPE", res.data.user._type);
		commit("SET_PROFILE_IMAGE", res.data.user.profileImage);
		commit(
			"SET_USER_COMMUNITIES",
			res.data.user.communities.map(comm => comm._id)
		);

		let query;
		let type = getters.type.toLowerCase();
		if (type === "staff") query = getSingleStaff;
		else if (type === "resident") query = getSingleResident;
		else if (type === "contact") {
			query = getSingleContact;
			type = "user";
		} else return null;

		const res2 = await apolloClient.query({
			query,
			fetchPolicy: "no-cache",
			variables: {
				id: res.data.user._id,
			},
		});

		commit("SET_DATA", res2.data[type]);

		return dispatch("getSSOUser", id);
	},

	async getSSOUser({ commit }, id) {
		// Get SSO user associations, roles and properties
		const { associations, properties, roles } = await api.getUser(id);
		commit("SET_SSO_ASSOCIATIONS", associations);
		commit("SET_SSO_PROPERTIES", properties);
		commit("SET_SSO_ROLES", roles);
	},

	hasChanged({ getters }) {
		const totalItemsChanged = _.size(getters.updated) + _.size(getters.updatedProperties);
		return totalItemsChanged > 0;
	},

	async handleUpdateUser({ commit, dispatch, getters }, id) {
		commit("SET_LOADING", true);
		let responses = [];
		const result = {};
		try {

			if (Object.keys(getters.updated).length) {
				responses.push(await dispatch("updateUser", id));
			}
			if (Object.keys(getters.updatedProperties).length) {
				responses.push(await dispatch("updateUserProperties", id));
			}

			if (responses.length) {
				await dispatch("getUser", id);
			}

			commit("SET_EDITABLE", false);
		} catch (err) {
			result.error = err.message;
		}
		commit("SET_LOADING", false);
		return result;
	},

	async updateUser({ commit, dispatch, getters }, id) {
		if (!Object.keys(getters.updated).length) {
			commit("SET_EDITABLE", false);
			return {};
		}

		let mutation;
		let type = getters.type.toLowerCase();
		let deletions = [];
		let fields = {
			// because these fields are required to update staff
			firstName: getters.staffPersonalInfo.firstName,
			lastName: getters.staffPersonalInfo.lastName,
			email: getters.staffPersonalInfo.email,
		};

		Object.keys(getters.updated).forEach(key => {
			const value = getters.updated[key];
			if (typeof value === "boolean" || value) fields[key] = value;
			else deletions.push(key);
		});

		if (type === "staff") mutation = updateStaff;
		else if (type === "resident") mutation = updateResident;
		else if (type === "contact") {
			mutation = updateContact;
			type = "user";
		}
		else throw new Error("Invalid user type.");

		return apolloClient.mutate({
			mutation,
			variables: {
				community: getters.community,
				id,
				fields,
				deletions,
			},
		});
	},

	async setProfileImage({ commit, getters }, { image, id }) {

		image = dataURLtoFile(image, "profile-image.png");

		const userType = getters.type.toLowerCase();

		try {
			const res = await apolloClient.mutate({
				mutation: setProfileImageMutation(userType),
				variables: { id, image },
			});

			commit("SET_PROFILE_IMAGE", res.data.community[userType].setProfileImage.profileImage);

			return res.data.community[userType].setProfileImage;
		} catch (err) {
			console.warn(err.message);
			if (err.message.includes("403")) {
				return {
					error: "You do not have the correct permissions to change the profile image of an user.",
				};
			}
			return {
				error: err.message,
			};
		}

	},

	async updateUserProperties({ commit, getters }, id) {
		if (!Object.keys(getters.updatedProperties).length) return {};

		const payload = Object.entries(getters.updatedProperties)
			.map(([name, value]) => ({ name, value }));

		return api.addUserProperties(id, payload);
	},

	async changeUserAssociationRole({ dispatch }, { method, userId, itemId = "" }) {
		const acceptedMethods = ["revokeUserRole", "addUserRole", "addAssociationUser", "removeAssociationUser"];

		if (!acceptedMethods.includes(method)) return;
		if (!Array.isArray(itemId)) itemId = [itemId];

		let res = [];
		for (const id of itemId) {
			res.push(api[method](userId, id));
		}

		res = await Promise.all(res);

		// TODO change to set only sso roles
		await dispatch("getSSOUser", userId);

		return res;
	},
};

export default {
	namespaced: true,
	state,
	getters,
	mutations,
	actions,
};
